import { Controller } from "@hotwired/stimulus"

// Initializes jQuery DataTables directly rather than through the shared `datatable` controller.
//
// `bFilter: false` disables DataTables' filtering outright, so despite the `f` token in the `dom`
// string below this page renders no search input at all - which makes the GA
// data-track/data-trackcategory lines in fnDrawCallback unreachable. The page's real filter UI is
// the separate `dynamic-filters` controller.
//
// `filterValue` carries the current request's filter, rendered onto the wrapper by the server and
// re-sent on every ajax draw, so a filter picked through `dynamic-filters` (a full page reload)
// stays applied across sorting and paging.
export default class extends Controller {
  static get targets() {
    return ["table"];
  }

  static get values() {
    return { source: String, filter: Object };
  }

  connect() {
    this.dataTable = $(this.tableTarget).dataTable({
      processing: true,
      serverSide: true,
      responsive: true,
      ordering: true,
      ajax: {
        url: this.sourceValue,
        data: (d) => { d.filter = this.filterValue; },
      },
      bFilter: false,
      pageLength: 25,
      autoWidth: false,
      dom: "<'row'<'col-sm-12'f>>" +
        "<'row'<'col-sm-12'tr>>" +
        "<'row'<'col-sm-5'><'col-sm-7'p>>",
      pagingType: "full_numbers",
      language: {
        search: "",
        emptyTable: "No data available.",
        searchPlaceholder: "Search by name or email",
      },
      // The original's `name` column also carried an `"orgnanization-name": "plan"` key --
      // not a real DataTables column option (the real one is `orderable`, set alongside it),
      // so it did nothing then and is dropped here rather than ported.
      columns: [
        { data: "id", className: "id", orderable: false },
        { data: "name", orderable: false },
        { data: "plan_name", className: "plan-name", orderable: false },
        { data: "start_date", className: "start-date", orderable: true },
        { data: "end_date", className: "end-date", orderable: true },
      ],
      fnDrawCallback: (settings) => {
        // `bFilter: false` means this input never renders (see the header comment). Guarded the
        // same way subscription_events_controller.js#onDraw guards its own nTableWrapper lookup:
        // a plain `querySelector` returns null where jQuery's empty set would have silently
        // no-opped on `.attr()`.
        const searchInput = settings.nTableWrapper.querySelector(".dataTables_filter input[type='search']");
        if (searchInput) {
          searchInput.setAttribute("data-track", "Search");
          searchInput.setAttribute("data-trackcategory", "Account Settings | Team");
        }

        // No stylesheet rule sets a competing `display: none` on .dataTables_paginate for this
        // table (checked app/assets/stylesheets), so clearing the inline style is a faithful
        // native equivalent of jQuery 3's .show() here -- see the jquery3-show-hazard lore entry
        // for when that would not be safe.
        const paginate = settings.nTableWrapper.querySelector(".dataTables_paginate");
        if (paginate) {
          paginate.style.display = settings._iDisplayLength > settings.fnRecordsDisplay() ? "none" : "";
        }
      },
    });
  }

  // A Turbo-cached page restore reconnects this controller, and `.dataTable()` throws against an
  // element that already has an instance.
  disconnect() {
    if ($.fn.DataTable.isDataTable(this.tableTarget)) {
      $(this.tableTarget).DataTable().destroy();
    }
  }
}
